import { TimeOverride } from "./timeOverrideUtils.js";
import { dayMs, secondMs } from "../timeConstants.js";

export const LongMonthLookup = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
export const ShortMonthLookup = ["Jan", "Feb", "Mar", "Apr", "May", "June", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const ShortDayLookup = ["Sun", "Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

export function AddEndingToDate(day: number): string {
    const lastDigit = day % 10;
    if (day > 10 && day < 14) {
        return day.toString() + "th";
    }
    if (lastDigit >= 4) {
        return day.toString() + "th";
    }
    switch (lastDigit) {
        case 1:
        case -1:
            return day.toString() + "st";
        case 2:
        case -2:
            return day.toString() + "nd";
        case 3:
        case -3:
            return day.toString() + "rd";
    }
    return day.toString() + "th";
}

/**
 * Converts a date to a UTC date string in the format "day-month-year". Month is zero indexed. This is a legacy function
 * that the indexing of irrigation events relies upon. Fixing this requires a small DB migration.
 *
 * Should never be presented as a human-readable string.
 * @param date - Date object or timestamp
 * @deprecated
 * @returns UTC date string
 */
export function utcDateString(date: number | Date): string {
    if (!(date instanceof Date)) {
        date = new Date(date);
    }
    return `${date.getUTCDate()}-${date.getUTCMonth()}-${date.getUTCFullYear()}`;
}

/**
 * Rebuilds a Date from its UTC calendar components, truncating to whole seconds.
 * This is a legacy helper relied upon by recurrence-rule (RRule) start times, which
 * expect second-precision UTC instants.
 *
 * @param t - The Date to convert
 * @returns A new Date built from the UTC components of `t`, with milliseconds dropped
 */
export function timeToUTCConversion(t: Date): Date {
    return new Date(
        Date.UTC(
            t.getUTCFullYear(),
            t.getUTCMonth(),
            t.getUTCDate(),
            t.getUTCHours(),
            t.getUTCMinutes(),
            t.getUTCSeconds(),
        ),
    );
}

export enum LAST_CONTACT_FALLBACK_MODE {
    CURRENT_TIME = "CURRENT_TIME",
    ORIGINAL_TIME = "ORIGINAL_TIME",
    ERROR = "ERROR",
}

/**
 * Updates the last contact timestamp with validation and fallback handling.
 *
 * This function ensures that last contact times are monotonically increasing and handles
 * edge cases where timestamps may be affected by clock drift or synchronization issues.
 * It's designed for device communication scenarios where timestamps must be reliable.
 *
 * **Validation Rules:**
 * - Last contact generally cannot decrease (takes max of original and new values)
 * - Detects significant backward time jumps (>1 day)
 * - Logs warnings for minor backward time jumps (>100ms) for monitoring
 * - Always rejects timestamps more than 3 seconds in the future (prevents clock drift)
 *
 * **Backward Time Jump Detection:**
 * - **Major jump (>1 day backward)**: Triggers fallback, assumes server clock issue
 * - **Minor jump (>100ms backward)**: Logs info message, continues with original timestamp
 * - Both scenarios indicate potential clock synchronization problems worth monitoring
 *
 * **Future Timestamp Handling:**
 * The function always rejects timestamps more than 3 seconds in the future. This 3-second
 * buffer accounts for minor clock drift and network latency between server and device, but
 * prevents accepting timestamps from devices with significant clock synchronization issues.
 *
 * **Fallback Behavior:**
 * When validation fails, the function behavior depends on the fallbackMode:
 * - **CURRENT_TIME**: Returns current server time (always returns a Date, safe for live updates)
 * - **ORIGINAL_TIME**: Returns the original timestamp unchanged (may return undefined if original was undefined)
 * - **ERROR**: Throws an error with detailed diagnostics (for strict validation requirements)
 *
 * **Important Edge Cases:**
 *
 * 1. **ORIGINAL_TIME with undefined original:**
 *    When using ORIGINAL_TIME fallback mode and the original timestamp is undefined, validation
 *    failures will return undefined. This preserves type safety and makes it explicit that no
 *    valid historical timestamp exists.
 *
 * 2. **ORIGINAL_TIME with invalid original (self-correction):**
 *    If the original timestamp itself is more than 3 seconds in the future (indicating a previous
 *    error or system clock issue), the function will attempt to self-correct by validating the new timestamp.
 *    This allows the system to recover from corrupted historical timestamps by accepting a valid new timestamp.
 *    If the new timestamp is also invalid, the fallback behavior will cause the function to return undefined.
 *    This self-correction is logged as an error for monitoring and investigation.
 *
 *    Note that this means sometimes this function may return undefined even if both new and original timestamps are
 *    defined
 *
 * @param originalTimestamp - The existing last contact timestamp, or undefined if none exists.
 *                           When undefined and validation fails with ORIGINAL_TIME mode,
 *                           the function returns undefined (not epoch).
 * @param newTimestamp - The proposed new last contact timestamp from the latest communication.
 * @param fallbackMode - Determines behavior when validation fails. Options:
 *                      - CURRENT_TIME: Use current server time (always returns Date)
 *                      - ORIGINAL_TIME: Preserve original value (may return undefined)
 *                      - ERROR: Throw detailed error
 *
 * @returns A validated Date object, or undefined when originalTimestamp is undefined and
 *          ORIGINAL_TIME fallback is used. The return type preserves the nullability of
 *          the originalTimestamp parameter for type safety.
 *
 * @throws {Error} When fallbackMode is ERROR and validation fails. Error message includes:
 *                 - Which validation check failed
 *                 - The problematic timestamp values
 *                 - Current time for comparison
 *                 - Diagnostic information to identify the root cause
 *
 * @example
 * // Basic usage with current time fallback (recommended - always returns Date)
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact,
 *   new Date(),
 *   LAST_CONTACT_FALLBACK_MODE.CURRENT_TIME
 * );
 * // updated is always a Date
 *
 * @example
 * // Preserve original timestamp when new data is suspect
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact,
 *   suspectTimestamp,
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // updated may be undefined if device.lastContact was undefined and suspectTimestamp failed validation
 *
 * @example
 * // Handle the edge case where original is undefined with ORIGINAL_TIME mode
 * const updated = getUpdatedLastContactTimestamp(
 *   device.lastContact, // might be undefined for new device
 *   uplinkTimestamp,
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // Provide fallback if needed
 * const finalTimestamp = updated ?? new Date(); // Use current time if undefined
 *
 * @example
 * // Self-correction example: corrupted original timestamp
 * // If device.lastContact is somehow in the future (e.g., from a previous system error),
 * // the function will attempt to recover by validating the new timestamp independently
 * const updated = getUpdatedLastContactTimestamp(
 *   corruptedFutureTimestamp, // e.g., 5 seconds in the future
 *   validNewTimestamp,         // valid current timestamp
 *   LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME
 * );
 * // Function logs an error and recursively validates validNewTimestamp against epoch
 * // Result: Returns validNewTimestamp if it's valid, allowing recovery from corruption
 *
 * @example
 * // Strict validation that throws on invalid timestamps
 * try {
 *   const updated = getUpdatedLastContactTimestamp(
 *     device.lastContact,
 *     uplinkTimestamp,
 *     LAST_CONTACT_FALLBACK_MODE.ERROR
 *   );
 * } catch (err) {
 *   logger.error('Invalid timestamp received from device', err);
 *   // Handle the error appropriately
 * }
 */
export function getUpdatedLastContactTimestamp(
    originalTimestamp: Date | undefined,
    newTimestamp: Date | undefined,
    fallbackMode: LAST_CONTACT_FALLBACK_MODE,
): Date | undefined {
    if (!originalTimestamp && !newTimestamp) {
        return undefined;
    }

    let useFallback: boolean = false;
    let errorMessage: string | undefined;

    const originalValue = originalTimestamp?.valueOf() ?? 0;
    const newValue = newTimestamp?.valueOf() ?? 0;
    const lastContactValue = Math.max(originalValue, newValue);
    const currentTimeValue = TimeOverride.valueOf();

    // only detect the last contact being wound back if both the original timestamp and the new timestamp are defined
    // if they are not defined, we only need to check we aren't advancing this into the future
    if (originalTimestamp && newTimestamp) {
        // Detect major backward time jump (>1 day) - likely indicates server clock issue
        if (originalValue > newValue + dayMs) {
            const backwardJump = originalValue - newValue;
            errorMessage =
                `Last contact timestamp validation failed: new timestamp is (${(backwardJump / dayMs).toFixed(1)} days) behind original. ` +
                `Original: ${new Date(originalValue).toISOString()}, ` +
                `New: ${new Date(newValue).toISOString()}. ` +
                `This likely indicates a server clock issue or system time adjustment.`;

            if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
                throw new Error(errorMessage);
            }
            console.warn(errorMessage);
            useFallback = true;
        }
        // Detect minor backward time jump (>100ms) - worth logging for monitoring
        else if (originalValue > newValue + 100) {
            const backwardJump = originalValue - newValue;
            console.info(
                `Last contact timestamp moving backward by ${backwardJump}ms. ` +
                    `Original: ${new Date(originalValue).toISOString()}, ` +
                    `New: ${new Date(newValue).toISOString()}. ` +
                    `This may indicate minor clock drift or out-of-order message processing.`,
            );
        }
    }

    // Always reject timestamps more than 3 seconds into the future to prevent clock drift issues
    if (lastContactValue > currentTimeValue + secondMs * 3) {
        const futureOffset = lastContactValue - currentTimeValue;
        errorMessage =
            `Last contact timestamp validation failed: timestamp is ${futureOffset}ms in the future (${(futureOffset / 1000).toFixed(1)}s). ` +
            `Last contact: ${new Date(lastContactValue).toISOString()}, ` +
            `Current time: ${new Date(currentTimeValue).toISOString()}, ` +
            `Maximum allowed future offset: 3000ms (3s). ` +
            `This likely indicates clock synchronization issues on the device.`;

        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
            throw new Error(errorMessage);
        }
        console.warn(errorMessage);
        useFallback = true;
    }

    if (useFallback) {
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.CURRENT_TIME) {
            return new Date(currentTimeValue);
        }
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ORIGINAL_TIME) {
            // Self-correction: If the original timestamp itself is invalid (>3s in future),
            // attempt to recover by validating the new timestamp against epoch instead.
            // This allows the system to recover from corrupted historical timestamps.
            if (originalValue > currentTimeValue + secondMs * 3) {
                console.error(
                    `Last contact timestamp self-correction triggered: ` +
                        `Original timestamp is ${originalValue - currentTimeValue}ms in the future (${((originalValue - currentTimeValue) / 1000).toFixed(1)}s). ` +
                        `Current time: ${new Date(currentTimeValue).toISOString()}. ` +
                        `Original lastContact timestamp: ${new Date(originalValue).toISOString()}, ` +
                        `new lastContact timestamp: ${new Date(newValue).toISOString()}. ` +
                        `Attempting to recover by validating new timestamp against epoch. ` +
                        `This indicates a previous system error or clock synchronization issue that requires investigation.`,
                );

                // Recursive call with undefined as the original - this validates newTimestamp on its own merits
                // If newTimestamp is also invalid, it will trigger fallback again (returning undefined for ORIGINAL_TIME)
                // If newTimestamp is valid, we successfully recover with a good timestamp
                return getUpdatedLastContactTimestamp(undefined, newTimestamp, fallbackMode);
            }

            // Return original timestamp as-is (may be undefined)
            // This preserves type safety and makes it explicit when no valid historical timestamp exists
            return originalTimestamp;
        }
        // this should not happen, error fallbacks should cause an early function exit. This is in case the code changes
        // in the future.
        if (fallbackMode === LAST_CONTACT_FALLBACK_MODE.ERROR) {
            throw new Error(
                "Unknown error validating last contact update. Should have thrown and had an early exit, " +
                    "but instead somehow made it this far.",
            );
        }
    }

    return new Date(lastContactValue);
}

/**
 * Formats a timestamp to a human readable string in the locale specific format
 * Example for en-CA, would return "Nov 26, 2025, 04:57 p.m."
 * @param epochTimeMs - The timestamp in milliseconds
 * @returns The formatted time string
 */
export function formatTime(epochTimeMs: number): string {
    return new Date(epochTimeMs).toLocaleString("en-CA", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}
